import { useState, useCallback, useEffect, useRef } from 'react';
import { FaSearch, FaTimes } from 'react-icons/fa';
import api from '@/services/api';
import type { Message } from '@/types';

interface MessageSearchProps {
  channelId: string;
  onSelectMessage?: (messageId: string) => void;
}

export default function MessageSearch({ channelId, onSelectMessage }: MessageSearchProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Message[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchBy, setSearchBy] = useState<'content' | 'sender'>('content');
  const [focusedResultIdx, setFocusedResultIdx] = useState(0);

  const listRef = useRef<HTMLDivElement | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);

  // Close modal on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        handleClose();
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await api.get(`/messages/channels/${channelId}/search`, {
        params: {
          query: searchQuery,
          searchBy,
        },
      });
      setSearchResults(response.data.messages || []);
    } catch (error) {
      console.error('Error searching messages:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [channelId, searchQuery, searchBy]);

  useEffect(() => {
    if (searchResults.length > 0) {
      setFocusedResultIdx(0);
    }
  }, [searchResults]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch();
    }, 500);

    return () => clearTimeout(timer);
  }, [handleSearch]);

  const handleClose = () => {
    setIsOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    setFocusedResultIdx(0);
  };

  const focusResult = (idx: number) => {
    const container = listRef.current;
    if (!container) return;
    const items = Array.from(container.querySelectorAll<HTMLElement>('[data-search-result]'));
    const el = items[idx];
    if (el) {
      el.focus();
      setFocusedResultIdx(idx);
    }
  };

  const handleResultsKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(
      (listRef.current || document).querySelectorAll<HTMLElement>('[data-search-result]'),
    );
    if (items.length === 0) return;
    const active = document.activeElement as HTMLElement;
    let idx = items.findIndex((el) => el === active);
    if (idx === -1) idx = focusedResultIdx;

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        e.preventDefault();
        focusResult((idx + 1) % items.length);
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        e.preventDefault();
        focusResult((idx - 1 + items.length) % items.length);
        break;
      case 'Home':
        e.preventDefault();
        focusResult(0);
        break;
      case 'End':
        e.preventDefault();
        focusResult(items.length - 1);
        break;
      case 'Enter':
      case ' ':
      case 'Spacebar':
        e.preventDefault();
        handleSelectResult(items[idx].dataset.messageId as string);
        break;
      case 'Escape':
        e.preventDefault();
        handleClose();
        break;
    }
  };

  const handleSelectResult = (messageId: string) => {
    if (onSelectMessage) {
      onSelectMessage(messageId);
    }
    handleClose();
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="p-2 text-brand-on-surface-variant dark:text-brand-on-surface-variant hover:bg-brand-background dark:hover:bg-brand-background rounded focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none"
        title="Search messages"
        aria-label="Search messages"
        aria-expanded={isOpen}
      >
        <FaSearch size={18} />
      </button>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-start justify-center pt-16 sm:pt-24 p-4">
      <div
        ref={modalRef}
        className="m3-dialog bg-brand-surface dark:bg-[#1a1740] rounded-3xl shadow-2xl w-full max-w-2xl border border-brand-outline-variant/60 dark:border-[#3730a3] overflow-hidden"
      >
        {/* Search Header */}
        <div className="p-4 sm:p-5 border-b border-brand-outline-variant/60 dark:border-[#3730a3]">
          <div className="flex items-center gap-3 bg-brand-surface-variant/50 dark:bg-black/20 px-4 py-2.5 rounded-full border border-brand-outline-variant/60 focus-within:border-brand-primary focus-within:ring-2 focus-within:ring-brand-primary/30 transition-all">
            <FaSearch className="text-primary-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search messages..."
              className="flex-1 bg-transparent outline-none text-brand-on-background dark:text-[#e0e7ff] text-sm sm:text-base placeholder-brand-outline"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  focusResult(0);
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  handleClose();
                }
              }}
            />
            <button
              onClick={handleClose}
              aria-label="Close search"
              className="p-1.5 hover:bg-white/10 rounded-full text-brand-outline hover:text-white active:scale-95 transition-all cursor-pointer"
            >
              <FaTimes aria-hidden="true" size={14} />
            </button>
          </div>

          {/* Search Type Selector */}
          <div className="mt-3 flex gap-2">
            <button
              onClick={() => setSearchBy('content')}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer active:scale-95 ${
                searchBy === 'content'
                  ? 'bg-primary-600/30 text-primary-300 border border-primary-500/40 shadow-sm'
                  : 'bg-brand-surface-variant/40 text-brand-outline hover:bg-white/10 hover:text-white'
              }`}
            >
              By Content
            </button>
            <button
              onClick={() => setSearchBy('sender')}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer active:scale-95 ${
                searchBy === 'sender'
                  ? 'bg-primary-600/30 text-primary-300 border border-primary-500/40 shadow-sm'
                  : 'bg-brand-surface-variant/40 text-brand-outline hover:bg-white/10 hover:text-white'
              }`}
            >
              By Sender
            </button>
          </div>
        </div>

        {/* Search Results */}
        <div
          className="max-h-96 overflow-y-auto p-4 space-y-2"
          ref={listRef}
          onKeyDown={handleResultsKeyDown}
          tabIndex={-1}
        >
          {isSearching ? (
            <div className="text-center py-8 text-brand-outline animate-pulse">Searching messages...</div>
          ) : searchQuery && searchResults.length === 0 ? (
            <div className="text-center py-8 text-brand-outline">No messages found</div>
          ) : searchResults.length > 0 ? (
            <div className="space-y-2">
              {searchResults.map((message, idx) => (
                <button
                  key={message.id}
                  data-search-result
                  data-message-id={message.id}
                  tabIndex={focusedResultIdx === idx ? 0 : -1}
                  onClick={() => handleSelectResult(message.id)}
                  onFocus={() => setFocusedResultIdx(idx)}
                  className="w-full text-left p-3.5 rounded-2xl bg-brand-surface/60 dark:bg-white/5 hover:bg-white/10 border border-transparent hover:border-primary-500/30 transition-all cursor-pointer active:scale-[0.99]"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm text-brand-on-background dark:text-[#e0e7ff]">
                      {message.sender?.username || 'Unknown'}
                    </span>
                    <span className="text-xs text-brand-outline">
                      {new Date(message.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="text-sm text-brand-on-surface dark:text-[#c4b5fd] line-clamp-2">
                    {message.content}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-brand-outline">
              {searchBy === 'content'
                ? 'Start typing to search messages...'
                : 'Start typing to search by sender...'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
